import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { article as articleData } from "shared/data/article";

import { getArticleType } from "scmp-app/components/article/article-render/helpers";
import { usePlusArticlePopup } from "scmp-app/components/plus/article-popup/hooks";
import type { highlightBlockItemContent$key } from "scmp-app/queries/__generated__/highlightBlockItemContent.graphql";

import { StyledContentItemPlusOpinion, StyledContentItemPlusPrimary } from "./styles";

export type Props = {
  reference: highlightBlockItemContent$key;
};

export const HighlightBlockItem: FunctionComponent<Props> = ({ reference }) => {
  const content = useFragment(
    graphql`
      fragment highlightBlockItemContent on Content {
        __typename
        entityId
        ...helpersGetArticleTypeArticle
        ...hooksPlusArticleLinkHandlerArticle
        ...plusOpinionContentItemContent
        ...plusPrimaryContentItemContent
        ...helpersCheckArticleIsInSectionsArticle
      }
    `,
    reference,
  );

  const { handleOpen } = usePlusArticlePopup();
  const articleTypeId = getArticleType(content) ?? "";

  const isOpinionArticle = useMemo(
    () =>
      articleTypeId === articleData.types.opinion.entityId ||
      articleTypeId === articleData.types.column.entityId,
    [articleTypeId],
  );

  // 'Page' type content is expected to redirect user to a new page
  // ref: handleOpen() -> checkCanShowInPlusPlatform() -> false
  if (content.__typename === "Page") {
    return (
      <StyledContentItemPlusPrimary
        onClick={() =>
          handleOpen(content, { pageType: "homepage", widgetName: "highlight_from_plus" })
        }
        reference={content}
      />
    );
  }

  if (isOpinionArticle)
    return (
      <StyledContentItemPlusOpinion
        onClick={() =>
          handleOpen(content, { pageType: "homepage", widgetName: "highlight_from_plus" })
        }
        reference={content}
        withAuthor
      />
    );

  return (
    <StyledContentItemPlusPrimary
      onClick={() =>
        handleOpen(content, { pageType: "homepage", widgetName: "highlight_from_plus" })
      }
      reference={content}
    />
  );
};

HighlightBlockItem.displayName = "HighlightBlockItem";
